/**
 * Opens WhatsApp with the given URL, falling back to web version if app is not installed
 * @param whatsappUrl - The WhatsApp URL (whatsapp://send?phone=...) from config
 * @returns Promise that resolves when WhatsApp is opened
 */
export const openWhatsApp = (whatsappUrl: string): Promise<void> => {
  return new Promise((resolve) => {
    // Check if we're on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Extract phone and text from the URL
    const urlParams = new URLSearchParams(whatsappUrl.split('?')[1]);
    const phone = urlParams.get('phone') || '';
    const text = urlParams.get('text') || '';
    
    // Clean phone number (remove non-numeric characters)
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Encode text for URL
    const encodedText = encodeURIComponent(text);
    
    if (isMobile) {
      // On mobile, try to open the app directly with wa.me (works better than whatsapp://)
      const waUrl = `https://wa.me/${cleanPhone}?text=${encodedText}`;
      const appUrl = `whatsapp://send?phone=${cleanPhone}&text=${text}`;
      
      // Use window.location.href for better app detection on mobile
      window.location.href = waUrl;
      resolve();
    } else {
      // On desktop, always use web version
      const webUrl = `https://web.whatsapp.com/send?phone=${cleanPhone}&text=${encodedText}`;
      window.open(webUrl, '_blank');
      resolve();
    }
  });
};

/**
 * Converts a whatsapp:// URL to web WhatsApp URL
 * @param whatsappUrl - The WhatsApp URL (whatsapp://send?phone=...)
 * @returns Web WhatsApp URL (https://wa.me/...)
 */
export const convertToWebWhatsApp = (whatsappUrl: string): string => {
  const urlParams = new URLSearchParams(whatsappUrl.split('?')[1]);
  const phone = urlParams.get('phone') || '';
  const text = urlParams.get('text') || '';
  
  const cleanPhone = phone.replace(/\D/g, '');
  
  return `https://wa.me/${cleanPhone}?text=${text}`;
};

